import { useEffect, useRef } from 'react'

/** Up, Up, Down, Down, Left, Right, Left, Right, B, A — the sequence as Konami shipped it in 1986. */
const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a']

/**
 * Calls `onUnlock` once the Konami code is typed anywhere in the page.
 *
 * The match is on `key`, so the letters follow the user's layout rather than the physical key, and
 * they are compared lowercased so Caps Lock does not break the run.
 *
 * It keeps the last ten keys rather than an index into the code, because the code starts with the
 * same key twice: an index would have to decide where to restart after a mismatch, and any simple
 * answer gets `Up Up Up Down Down ...` wrong. Comparing the tail costs ten string compares a
 * keystroke and is right whatever was typed before.
 *
 * `enabled` false binds no listener at all, rather than binding one that drops what it hears: someone
 * who is not meant to find the code should not have their keystrokes read on the way to being ignored.
 */
export const useKonamiCode = (onUnlock: () => void, { enabled = true }: { enabled?: boolean } = {}) => {
  const typed = useRef<string[]>([])
  // The handler is bound once, so it must not close over a stale `onUnlock` from the first render.
  const callback = useRef(onUnlock)
  useEffect(() => {
    callback.current = onUnlock
  })

  useEffect(() => {
    if (!enabled) return

    const onKeyDown = (event: KeyboardEvent) => {
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key
      typed.current = [...typed.current, key].slice(-KONAMI_CODE.length)

      if (typed.current.length === KONAMI_CODE.length && typed.current.every((pressed, index) => pressed === KONAMI_CODE[index])) {
        typed.current = []
        callback.current()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}
